import React, { createContext, useContext, useState, useCallback } from 'react';
import { resolveWordTranslationHierarchy, ResolvedWordTranslation } from '../services/richDictionaryService';
import { UnifiedDictionaryModal, UnifiedWordTarget } from '../components/UnifiedDictionaryModal';
import { textToSpeechService } from '../services/textToSpeechService';

export interface UnifiedDictionaryContextType {
  target: UnifiedWordTarget | null;
  resolvedData: ResolvedWordTranslation | null;
  loading: boolean;
  isExpanded: boolean;
  handleWordClick: (
    e: any,
    word: string,
    sourceLang?: string,
    targetLang?: string,
    preserveExpanded?: boolean,
    silent?: boolean
  ) => Promise<void>;
  closeDictionary: () => void;
  openExpandedModal: () => void;
}

const UnifiedDictionaryContext = createContext<UnifiedDictionaryContextType | null>(null);

export const UnifiedDictionaryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [target, setTarget] = useState<UnifiedWordTarget | null>(null);
  const [resolvedData, setResolvedData] = useState<ResolvedWordTranslation | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const handleWordClick = useCallback(async (
    e: any,
    word: string,
    sourceLang: string = 'en',
    targetLang: string = 'pt',
    preserveExpanded: boolean = false,
    silent: boolean = false
  ) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    const cleanWord = word.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
    if (!cleanWord) return;

    let x = typeof window !== 'undefined' ? window.innerWidth / 2 : 0;
    let y = typeof window !== 'undefined' ? window.innerHeight / 2 : 0;

    if (e && e.currentTarget && typeof e.currentTarget.getBoundingClientRect === 'function') {
      const rect = e.currentTarget.getBoundingClientRect();
      x = rect.left + rect.width / 2;
      y = rect.top;
    } else if (e && typeof e.clientX === 'number') {
      x = e.clientX;
      y = e.clientY;
    }

    setTarget({ word: cleanWord, sourceLang, targetLang, x, y });
    setLoading(true);
    setResolvedData(null);
    if (!preserveExpanded) {
      setIsExpanded(false);
    }

    // Speak word via TTS natively (exceto em telas com som desligado, ex.: Voz Ativa no mudo)
    if (!silent) textToSpeechService.speak(cleanWord, sourceLang);

    try {
      const res = await resolveWordTranslationHierarchy(cleanWord, targetLang, sourceLang);
      setResolvedData(res);
    } catch (err) {
      console.warn("[UnifiedDictionaryContext] Translation error:", err);
      setResolvedData({
        word: cleanWord,
        primaryTranslation: cleanWord,
        translations: [cleanWord],
        sourceLang,
        targetLang,
        source: 'legacy-r2'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const closeDictionary = useCallback(() => {
    setTarget(null);
    setResolvedData(null);
    setLoading(false);
    setIsExpanded(false);
  }, []);

  const openExpandedModal = useCallback(() => {
    setIsExpanded(true);
  }, []);

  return (
    <UnifiedDictionaryContext.Provider
      value={{
        target,
        resolvedData,
        loading,
        isExpanded,
        handleWordClick,
        closeDictionary,
        openExpandedModal
      }}
    >
      {children}
      <UnifiedDictionaryModal
        resolvedData={resolvedData}
        loading={loading}
        target={target}
        onClose={closeDictionary}
        isOpenExpandedModal={isExpanded}
        onOpenExpandedModal={openExpandedModal}
        onLookupWord={(word, src, tgt) => handleWordClick(null, word, src || target?.sourceLang || 'en', tgt || target?.targetLang || 'pt', true)}
      />
    </UnifiedDictionaryContext.Provider>
  );
};

export const useUnifiedDictionary = (): UnifiedDictionaryContextType => {
  const ctx = useContext(UnifiedDictionaryContext);
  if (!ctx) {
    return {
      target: null,
      resolvedData: null,
      loading: false,
      isExpanded: false,
      handleWordClick: async () => {},
      closeDictionary: () => {},
      openExpandedModal: () => {}
    };
  }
  return ctx;
};
